import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InvestpackService } from '@app/investment/investpack.service';
import { AlertService } from '@service/alert.service';
import { DialogService } from '@service/dialog.service';
import { FireAnalyticsService } from '@service/fire-analytics.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-external-order',
  templateUrl: './external-order.component.html',
  styles: [
  ]
})
export class ExternalOrderComponent implements OnInit {

  public allInstruments: any[] = [];
  externalInvestmntFOrm: FormGroup;
  isLoading: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<ExternalOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { investpack: any, subscription: any, section: string, rebalanceData: any[] },
    private formBuilder: FormBuilder,
    private investpackService: InvestpackService,
    private alertService: AlertService,
    private analytics: FireAnalyticsService,
    private dialogService: DialogService
  ) { }

  ngOnInit(): void {
    if (this.data.rebalanceData.length) {
      if (this.data.subscription.isInvested && this.data.subscription.isRebalancing) {
        this.allInstruments = this.data.rebalanceData.filter(x => x.activity)
      } else {
        this.allInstruments = this.data.rebalanceData
      }
    } else {
      this.allInstruments = this.data.investpack.instruments.filter(x => x.active && x.quantity > 0)
    }
    this.initStocksForm()
  }

  close(value: boolean) {
    this.dialogRef.close(value)
  }

  initStocksForm() {
    this.externalInvestmntFOrm = this.formBuilder.group({
      cash: this.formBuilder.array([], this.quantityValidator.bind(this)),
      gold: this.formBuilder.array([], this.quantityValidator.bind(this)),
      etf: this.formBuilder.array([], this.quantityValidator.bind(this)),
      mutual_fund: this.formBuilder.array([], this.quantityValidator.bind(this))
    })
    this.allInstruments.forEach((instrument) => {
      instrument.quantity = parseFloat(instrument.quantity.toFixed(2));
      if (this.data.section === 'rebalance' && instrument.activity) {
        if (instrument.category === 'cash' && !instrument.isGold && !instrument.isEtf) {
          this.addInstrument('cash', instrument)
        } else if (instrument.category === 'cash' && instrument.isGold && !instrument.isEtf) {
          this.addInstrument('gold', instrument)
        } else if (instrument.category === 'cash' && !instrument.isGold && instrument.isEtf) {
          this.addInstrument('etf', instrument)
        } else {
          this.addInstrument('mutual_fund', instrument)
        }
      } else if (instrument.quantity > 0) {
        if (instrument.category === 'cash' && !instrument.isGold && !instrument.isEtf) {
          this.addInstrument('cash', instrument)
        } else if (instrument.category === 'cash' && instrument.isGold && !instrument.isEtf) {
          this.addInstrument('gold', instrument)
        } else if (instrument.category === 'cash' && !instrument.isGold && instrument.isEtf) {
          this.addInstrument('etf', instrument)
        } else {
          this.addInstrument('mutual_fund', instrument)
        }
      }
    })
  }

  newInstrument(instrument) {
    const form = this.formBuilder.group({
      name: [instrument.name, Validators.required],
      tradingSymbol: [instrument.category === 'mutual_fund' ? instrument.name : instrument.tradingSymbol, Validators.required],
      category: [instrument.category, Validators.required],
      instrument: [instrument.category === 'mutual_fund' ? instrument.isinNo : instrument.instrumentToken, Validators.required],
      currentPrice: [instrument.currentPrice, Validators.required],
      quantity: [instrument.quantity, Validators.required],
      scriptType: [instrument.scriptType ? instrument.scriptType : 'BUY', Validators.required],
      weight: [instrument.weight ? instrument.weight : 0, Validators.required]
    })
    if (this.data.section === 'rebalance') {
      form.controls['currentPrice'].disable()
      form.controls['quantity'].disable()
    }
    return form
  }

  addInstrument(contol, formData): void {
    (this.externalInvestmntFOrm.get(contol) as FormArray).push(this.newInstrument(formData));
  }

  getControlsArr(control) {
    return this.externalInvestmntFOrm.get(control) as FormArray
  }

  quantityValidator(array: FormArray) {
    if (this.externalInvestmntFOrm) {
      for (let item of this.allInstruments) {
        for (let x of array.controls) {
          if (x.get('instrument').value === item._id && x.get('quantity').value < item.quantity && !x.get('quantity').hasError('required')) {
            x.get('quantity').setErrors({
              target: "Quantity cannot be lesser than suggested initial quantity",
            })
          }
        }
      }
    }
  }

  submitExternalInvestment() {
    if (this.data.section === 'rebalance') {
      this.externalRebalance()
    } else {
      this.externalOrders()
    }
  }

  confirmInvestment() {
    this.dialogService.openCommonDialog({}, {
      title: 'Are you sure?',
      content: `You want to invest externally.`,
      cancelBtn: 'No',
      okBtn: 'Yes'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.submitExternalInvestment();
      }
    });
  }

  externalOrders() {
    this.isLoading = true
    let instruments: any[] = []
    let totalInsvestment = 0
    this.allInstruments.forEach(element => {
      if (element.quantity > 0) {
        let instruData = {
          _id: element._id,
          tradingSymbol: element.tradingSymbol,
          exchange: element.exchange,
          quantity: element.quantity,
          investment: element.investment,
          scriptType: "BUY",
          orderStatus: "success",
          validity: "DAY",
          price: element.currentPrice,
          category: element.category,
          weight: element.weight ? element.weight : 0
        }
        if (element.scriptType) {
          instruData.scriptType = element.scriptType
        } else {
          if (element.newQuantity) {
            if ((element.newQuantity - element.quantity) > 0) {
              instruData.scriptType = "BUY"
            } else {
              instruData.scriptType = "SELL"
            }
          } else {
            instruData.scriptType = "BUY"
          }
        }
        let category: string = 'cash'
        if (element.category === 'cash' && !element.isGold && !element.isEtf) {
          category = 'cash'
        } else if (element.category === 'cash' && element.isGold && !element.isEtf) {
          category = 'gold'
        } else if (element.category === 'cash' && !element.isGold && element.isEtf) {
          category = 'etf'
        } else {
          category = 'mutual_fund'
        }
        let formData: any;
        if (category === 'mutual_fund') {
          formData = this.externalInvestmntFOrm.controls['mutual_fund'].value.find(x => x.instrument === element.isinNo)
        } else {
          formData = this.externalInvestmntFOrm.controls[category].value.find(x => x.instrument === element.instrumentToken)
        }
        instruData.quantity = formData?.quantity ?? instruData.quantity;
        instruData.price = formData?.currentPrice ?? instruData.price;
        totalInsvestment += ((formData.quantity ?? 0) * formData.currentPrice)
        instruments.push(instruData)
      }
    });

    let data = {
      subscription: this.data.subscription._id,
      platform: "other",
      investments: totalInsvestment,
      instruments: instruments,
      status: "success"
    }

    this.investpackService.createExternalOrders(data)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(
        (res) => {
          this.analytics.logEvents('Submit External Investment', { investpack: this.data.investpack.name })
          this.alertService.success(res.message)
          window.localStorage.setItem("holdingmsg", 'true');
          this.dialogRef.close(true)
        }
      )
  }

  externalRebalance() {
    this.isLoading = true;
    let instruments: any[] = []
    let totalInsvestment = 0
    this.allInstruments.forEach((element) => {
      if (element.quantity > 0) {
        let category: any = 'cash';
        if (element.category === 'cash' && !element.isGold && !element.isEtf) {
          category = 'cash'
        } else if (element.category === 'cash' && element.isGold && !element.isEtf) {
          category = 'gold'
        } else if (element.category === 'cash' && !element.isGold && element.isEtf) {
          category = 'etf'
        } else {
          category = 'mutual_fund'
        }
        let instruData = {
          tradingSymbol: element.tradingSymbol,
          exchange: element.exchange,
          quantity: element.quantity,
          investment: element.investment,
          scriptType: "BUY",
          orderStatus: "success",
          validity: "DAY",
          price: element.currentPrice,
          category: element.category,
          weight: element.weight ? element.weight : 0
        }
        if (element.scriptType) {
          instruData.scriptType = element.scriptType
        } else {
          if (element.newQuantity) {
            if ((element.newQuantity - element.quantity) > 0) {
              instruData.scriptType = "BUY"
            } else {
              instruData.scriptType = "SELL"
            }
          } else {
            instruData.scriptType = "BUY"
          }
        }
        let formData: any;
        if (category === 'mutual_fund') {
          formData = this.externalInvestmntFOrm.controls['mutual_fund'].value.find(x => x.instrument === element.isinNo)
        } else {
          formData = this.externalInvestmntFOrm.controls[category].value.find(x => x.instrument === element.instrumentToken)
        }
        instruData.quantity = formData?.quantity ?? instruData.quantity;
        instruData.price = formData?.currentPrice ?? instruData.price;
        totalInsvestment += ((formData.quantity ?? 0) * formData.currentPrice)
        instruments.push(instruData)
      }
    })
    let data = {
      wealthcase: this.data.investpack._id,
      subscription: this.data.subscription._id,
      platform: "other",
      investments: totalInsvestment,
      instruments: instruments,
      status: "success"
    }
    this.investpackService.alreadyRebalanced(data)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(
        (res) => {
          this.analytics.logEvents('Rebalance Investment Externally', { investpack: this.data.investpack.name })
          this.alertService.success(res.message)
          window.localStorage.setItem("holdingmsg", 'true');
          this.dialogRef.close(true)
        }
      )
  }
}
